import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Layout from '../components/Layout';
import MiniDashboard from '../components/MiniDashboard';
import { formatDate, formatDateTime } from '../utils/formatters';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

function Announcements({ user, onLogout }) {
  const [announcements, setAnnouncements] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [selectedAnnouncement, setSelectedAnnouncement] = useState(null);
  const [filterPriority, setFilterPriority] = useState('');
  const [dashboardStats, setDashboardStats] = useState(null);
  
  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'Normal',
    target_audience: ['All'],
    expires_at: ''
  });

  useEffect(() => {
    fetchAnnouncements();
    fetchDashboardStats();
    if (user.role === 'Admin' || user.role === 'HR Manager') {
      fetchDepartments();
    }
  }, [filterPriority]);

  const fetchDashboardStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/dashboard/stats`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDashboardStats(response.data);
    } catch (error) {
      console.error('Failed to fetch dashboard stats:', error);
    }
  };

  const fetchDepartments = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/departments`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setDepartments(response.data);
    } catch (error) {
      console.error('Failed to fetch departments:', error);
    }
  };

  const fetchAnnouncements = async () => {
    try {
      const token = localStorage.getItem('token');
      let url = `${API}/announcements`;
      if (filterPriority) url += `?priority=${filterPriority}`;
      
      const response = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAnnouncements(response.data);
    } catch (error) {
      console.error('Failed to fetch announcements:', error);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const submitData = {
        ...formData,
        expires_at: formData.expires_at ? new Date(formData.expires_at).toISOString() : null
      };

      if (selectedAnnouncement) {
        await axios.put(`${API}/announcements/${selectedAnnouncement.id}`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Announcement updated successfully!');
      } else {
        await axios.post(`${API}/announcements`, submitData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        alert('Announcement created successfully!');
      }

      setShowForm(false);
      setSelectedAnnouncement(null);
      resetForm();
      fetchAnnouncements();
    } catch (error) {
      alert('Failed to save announcement: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleEdit = (announcement) => {
    setSelectedAnnouncement(announcement);
    setFormData({
      title: announcement.title,
      content: announcement.content,
      priority: announcement.priority,
      target_audience: announcement.target_audience || ['All'],
      expires_at: announcement.expires_at ? announcement.expires_at.split('T')[0] : ''
    });
    setShowForm(true);
  };

  const handleDelete = async (announcementId) => {
    if (!window.confirm('Are you sure you want to delete this announcement?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/announcements/${announcementId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Announcement deleted successfully!');
      fetchAnnouncements();
    } catch (error) {
      alert('Failed to delete announcement: ' + (error.response?.data?.detail || error.message));
    }
  };

  const handleDeactivate = async (announcementId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/announcements/${announcementId}/deactivate`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      alert('Announcement deactivated successfully!');
      fetchAnnouncements();
    } catch (error) {
      alert('Failed to deactivate announcement: ' + (error.response?.data?.detail || error.message));
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      content: '',
      priority: 'Normal',
      target_audience: ['All'],
      expires_at: ''
    });
  };

  const handleTargetAudienceChange = (value) => {
    if (value === 'All') {
      setFormData({ ...formData, target_audience: ['All'] });
    } else {
      const current = formData.target_audience.filter(t => t !== 'All');
      if (current.includes(value)) {
        setFormData({ ...formData, target_audience: current.filter(t => t !== value) });
      } else {
        setFormData({ ...formData, target_audience: [...current, value] });
      }
    }
  };

  const getPriorityBadge = (priority) => {
    const styles = {
      'High': 'bg-red-100 text-red-700',
      'Normal': 'bg-[#2b338030] text-blue-700',
      'Low': 'bg-gray-100 text-gray-700'
    };
    return styles[priority] || 'bg-gray-100 text-gray-700';
  };

  const getPriorityIcon = (priority) => {
    const icons = {
      'High': '🔴',
      'Normal': '🔵',
      'Low': '⚪'
    };
    return icons[priority] || '⚪';
  };

  const isExpired = (expiresAt) => {
    if (!expiresAt) return false;
    return new Date(expiresAt) < new Date();
  };

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold" style={{color: '#2b3380'}}>Announcements</h1>
          {(user.role === 'Admin' || user.role === 'HR Manager') && (
            <button
              onClick={() => { setShowForm(true); setSelectedAnnouncement(null); resetForm(); }}
              className="px-6 py-2 rounded-lg text-white hover:opacity-90"
              style={{backgroundColor: '#2b3380'}}
            >
              Create Announcement
            </button>
          )}
        </div>

        {/* Announcements Mini Dashboard */}
        {dashboardStats && (
          <MiniDashboard
            title="Announcements Overview"
            icon="📢"
            bgColor="#3f51b5"
            stats={[
              {
                label: 'Active Announcements',
                value: dashboardStats.active_announcements || 0,
                subtitle: 'Last 30 days',
                color: '#3f51b5'
              },
              {
                label: 'Total Employees',
                value: dashboardStats.total_employees || 0,
                subtitle: 'Recipients',
                color: '#2b3380'
              },
              {
                label: 'Departments',
                value: dashboardStats.total_departments || 0,
                subtitle: 'Target groups',
                color: '#7e57c2'
              },
              {
                label: 'Priority Levels',
                value: '3',
                subtitle: 'High/Normal/Low',
                color: '#607d8b'
              }
            ]}
          />
        )}

        {/* Filter */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="flex items-center gap-4">
            <label className="font-medium text-gray-700">Filter by Priority:</label>
            <select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Priorities</option>
              <option value="High">High</option>
              <option value="Normal">Normal</option>
              <option value="Low">Low</option>
            </select>
          </div>
        </div>

        {/* Form Modal */}
        {showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                    {selectedAnnouncement ? 'Edit Announcement' : 'Create Announcement'}
                  </h2>
                  <button
                    onClick={() => { setShowForm(false); setSelectedAnnouncement(null); }}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">Title *</label>
                    <input
                      type="text"
                      value={formData.title}
                      onChange={(e) => setFormData({...formData, title: e.target.value})}
                      required
                      placeholder="Announcement title"
                      className="w-full px-3 py-2 border rounded-lg"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-1">Content *</label>
                    <textarea
                      value={formData.content}
                      onChange={(e) => setFormData({...formData, content: e.target.value})}
                      required
                      rows="6"
                      placeholder="Write your announcement here..."
                      className="w-full px-3 py-2 border rounded-lg"
                    ></textarea>
                  </div>

                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium mb-1">Priority *</label>
                      <select
                        value={formData.priority}
                        onChange={(e) => setFormData({...formData, priority: e.target.value})}
                        required
                        className="w-full px-3 py-2 border rounded-lg"
                      >
                        <option value="High">High</option>
                        <option value="Normal">Normal</option>
                        <option value="Low">Low</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium mb-1">Expires At (Optional)</label>
                      <input
                        type="date"
                        value={formData.expires_at}
                        onChange={(e) => setFormData({...formData, expires_at: e.target.value})}
                        className="w-full px-3 py-2 border rounded-lg"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium mb-2">Target Audience *</label>
                    <div className="bg-gray-50 p-4 rounded-lg space-y-2">
                      <label className="flex items-center">
                        <input
                          type="checkbox"
                          checked={formData.target_audience.includes('All')}
                          onChange={() => handleTargetAudienceChange('All')}
                          className="mr-2"
                        />
                        <span className="font-medium">All Employees</span>
                      </label>
                      
                      {!formData.target_audience.includes('All') && (
                        <>
                          <div className="mt-3 mb-2 font-medium text-sm">By Role:</div>
                          {['Admin', 'HR Manager', 'Employee'].map(role => (
                            <label key={role} className="flex items-center ml-4">
                              <input
                                type="checkbox"
                                checked={formData.target_audience.includes(role)}
                                onChange={() => handleTargetAudienceChange(role)}
                                className="mr-2"
                              />
                              {role}
                            </label>
                          ))}

                          <div className="mt-3 mb-2 font-medium text-sm">By Department:</div>
                          {departments.map(dept => (
                            <label key={dept.id} className="flex items-center ml-4">
                              <input
                                type="checkbox"
                                checked={formData.target_audience.includes(dept.id)}
                                onChange={() => handleTargetAudienceChange(dept.id)}
                                className="mr-2"
                              />
                              {dept.name}
                            </label>
                          ))}
                        </>
                      )}
                    </div>
                  </div>

                  <div className="flex gap-3 justify-end pt-4">
                    <button
                      type="button"
                      onClick={() => { setShowForm(false); setSelectedAnnouncement(null); }}
                      className="px-6 py-2 border rounded-lg hover:bg-gray-50"
                    >
                      Cancel
                    </button>
                    <button
                      type="submit"
                      className="px-6 py-2 rounded-lg text-white"
                      style={{backgroundColor: '#2b3380'}}
                    >
                      {selectedAnnouncement ? 'Update' : 'Create'} Announcement
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        )}

        {/* View Announcement Modal */}
        {selectedAnnouncement && !showForm && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
              <div className="p-6">
                <div className="flex justify-between items-center mb-4">
                  <div className="flex items-center gap-3">
                    <span className="text-3xl">{getPriorityIcon(selectedAnnouncement.priority)}</span>
                    <h2 className="text-2xl font-bold" style={{color: '#2b3380'}}>
                      {selectedAnnouncement.title}
                    </h2>
                  </div>
                  <button
                    onClick={() => setSelectedAnnouncement(null)}
                    className="text-gray-500 hover:text-gray-700 text-2xl"
                  >
                    ×
                  </button>
                </div>

                <div className="mb-4 flex gap-2">
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityBadge(selectedAnnouncement.priority)}`}>
                    {selectedAnnouncement.priority} Priority
                  </span>
                  {isExpired(selectedAnnouncement.expires_at) && (
                    <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                      Expired
                    </span>
                  )}
                </div>

                <div className="bg-gray-50 p-4 rounded-lg mb-4">
                  <p className="text-gray-700 whitespace-pre-wrap">{selectedAnnouncement.content}</p>
                </div>

                <div className="text-sm text-gray-600 space-y-1 border-t pt-4">
                  <p><span className="font-medium">Posted by:</span> {selectedAnnouncement.creator_name || 'Unknown'}</p>
                  <p><span className="font-medium">Date:</span> {formatDateTime(selectedAnnouncement.created_at)}</p>
                  {selectedAnnouncement.expires_at && (
                    <p><span className="font-medium">Expires:</span> {formatDateTime(selectedAnnouncement.expires_at)}</p>
                  )}
                  <p>
                    <span className="font-medium">Target:</span>{' '}
                    {selectedAnnouncement.target_audience.includes('All') 
                      ? 'All Employees' 
                      : selectedAnnouncement.target_audience.join(', ')}
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Announcements List */}
        <div className="space-y-4">
          {announcements.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-12 text-center text-gray-500">
              <p className="text-lg">No announcements to display.</p>
            </div>
          ) : (
            announcements.map((announcement) => (
              <div
                key={announcement.id}
                className="bg-white rounded-lg shadow-md p-6 hover:shadow-lg transition-shadow cursor-pointer"
                onClick={() => setSelectedAnnouncement(announcement)}
              >
                <div className="flex justify-between items-start">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className="text-2xl">{getPriorityIcon(announcement.priority)}</span>
                      <h3 className="text-xl font-bold" style={{color: '#2b3380'}}>
                        {announcement.title}
                      </h3>
                      <span className={`px-3 py-1 rounded-full text-xs font-medium ${getPriorityBadge(announcement.priority)}`}>
                        {announcement.priority}
                      </span>
                      {isExpired(announcement.expires_at) && (
                        <span className="px-3 py-1 rounded-full text-xs font-medium bg-red-100 text-red-700">
                          Expired
                        </span>
                      )}
                    </div>
                    
                    <p className="text-gray-700 mb-3 line-clamp-2">{announcement.content}</p>
                    
                    <div className="flex items-center gap-4 text-sm text-gray-500">
                      <span>Posted by: {announcement.creator_name || 'Unknown'}</span>
                      <span>•</span>
                      <span>{formatDate(announcement.created_at)}</span>
                      {announcement.expires_at && (
                        <>
                          <span>•</span>
                          <span>Expires: {formatDate(announcement.expires_at)}</span>
                        </>
                      )}
                    </div>
                  </div>

                  {(user.role === 'Admin' || user.role === 'HR Manager') && (
                    <div className="flex gap-2 ml-4" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => handleEdit(announcement)}
                        className="text-[#2b3380] hover:text-[#2b3380] text-sm font-medium"
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeactivate(announcement.id)}
                        className="text-orange-600 hover:text-orange-800 text-sm font-medium"
                      >
                        Deactivate
                      </button>
                      <button
                        onClick={() => handleDelete(announcement.id)}
                        className="text-red-600 hover:text-red-800 text-sm font-medium"
                      >
                        Delete
                      </button>
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </Layout>
  );
}

export default Announcements;
